import { getSupabaseClient } from "./index";
import type {
  AffiliateRecord,
  AffiliateReferralRecord,
  AffiliateRewardType,
  CouponRecord,
  CouponType,
  LoyaltyLedgerEntry,
  LoyaltySettings,
  LoyaltyStats,
} from "@veorest/utils";

export type {
  AffiliateRecord,
  AffiliateReferralRecord,
  CouponRecord,
  LoyaltyLedgerEntry,
  LoyaltySettings,
  LoyaltyStats,
};

const DEFAULT_SETTINGS: Omit<LoyaltySettings, "tenant_id" | "updated_at"> = {
  is_enabled: true,
  points_per_unit: 1,
  spend_unit: 1,
  min_order_total: 0,
  welcome_bonus_points: 0,
};

export async function getLoyaltySettings(tenantId: string): Promise<LoyaltySettings> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("loyalty_settings")
    .select("*")
    .eq("tenant_id", tenantId)
    .maybeSingle();

  if (error) throw error;

  if (!data) {
    return {
      tenant_id: tenantId,
      updated_at: new Date().toISOString(),
      ...DEFAULT_SETTINGS,
    };
  }

  return data as LoyaltySettings;
}

export async function upsertLoyaltySettings(
  tenantId: string,
  patch: Partial<Omit<LoyaltySettings, "tenant_id" | "updated_at">>
): Promise<LoyaltySettings> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("loyalty_settings")
    .upsert(
      {
        tenant_id: tenantId,
        ...patch,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "tenant_id" }
    )
    .select("*")
    .single();

  if (error) throw error;
  return data as LoyaltySettings;
}

export async function getLoyaltyStats(tenantId: string): Promise<LoyaltyStats> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_loyalty_stats", { p_tenant_id: tenantId });
  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  return {
    points_issued: Number(row?.points_issued ?? 0),
    active_affiliates: Number(row?.active_affiliates ?? 0),
    active_coupons: Number(row?.active_coupons ?? 0),
    referrals_this_month: Number(row?.referrals_this_month ?? 0),
  };
}

export async function listLoyaltyLedger(
  tenantId: string,
  limit = 50
): Promise<LoyaltyLedgerEntry[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("loyalty_points_ledger")
    .select("*, customers(name, phone)")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data ?? []).map((row) => {
    const customer = row.customers as { name?: string; phone?: string } | null;
    const { customers: _c, ...entry } = row as Record<string, unknown>;
    return {
      ...(entry as unknown as LoyaltyLedgerEntry),
      customer_name: customer?.name,
      customer_phone: customer?.phone,
    };
  });
}

export async function listAffiliates(tenantId: string): Promise<AffiliateRecord[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("affiliates")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as AffiliateRecord[];
}

export interface CreateAffiliateInput {
  tenantId: string;
  name: string;
  code: string;
  reward_type: AffiliateRewardType;
  reward_value?: number;
  commission_percent?: number;
  new_customer_only?: boolean;
  customer_id?: string | null;
  phone?: string | null;
  email?: string | null;
}

export async function createAffiliate(input: CreateAffiliateInput): Promise<AffiliateRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("affiliates")
    .insert({
      tenant_id: input.tenantId,
      name: input.name.trim(),
      code: input.code.trim().toUpperCase(),
      reward_type: input.reward_type,
      reward_value: input.reward_value ?? 0,
      commission_percent: input.commission_percent ?? 0,
      new_customer_only: input.new_customer_only ?? true,
      customer_id: input.customer_id ?? null,
      phone: input.phone?.trim() || null,
      email: input.email?.trim() || null,
      is_active: true,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data as AffiliateRecord;
}

export async function updateAffiliate(
  id: string,
  patch: Partial<
    Pick<
      AffiliateRecord,
      | "name"
      | "code"
      | "reward_type"
      | "reward_value"
      | "commission_percent"
      | "new_customer_only"
      | "customer_id"
      | "phone"
      | "email"
      | "is_active"
    >
  >
): Promise<AffiliateRecord> {
  const supabase = getSupabaseClient();
  const payload: Record<string, unknown> = { ...patch, updated_at: new Date().toISOString() };
  if (patch.code !== undefined) payload.code = patch.code.trim().toUpperCase();
  if (patch.name !== undefined) payload.name = patch.name.trim();

  const { data, error } = await supabase
    .from("affiliates")
    .update(payload)
    .eq("id", id)
    .select("*")
    .single();

  if (error) throw error;
  return data as AffiliateRecord;
}

export async function deleteAffiliate(id: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from("affiliates").delete().eq("id", id);
  if (error) throw error;
}

export async function listAffiliateReferrals(
  tenantId: string,
  affiliateId?: string,
  limit = 30
): Promise<AffiliateReferralRecord[]> {
  const supabase = getSupabaseClient();
  let query = supabase
    .from("affiliate_referrals")
    .select("*, customers(name), orders(total)")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (affiliateId) query = query.eq("affiliate_id", affiliateId);

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((row) => {
    const customer = row.customers as { name?: string } | null;
    const order = row.orders as { total?: number } | null;
    const { customers: _c, orders: _o, ...entry } = row as Record<string, unknown>;
    return {
      ...(entry as unknown as AffiliateReferralRecord),
      customer_name: customer?.name,
      order_total: order?.total != null ? Number(order.total) : undefined,
    };
  });
}

export async function listCoupons(tenantId: string): Promise<CouponRecord[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("coupons")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as CouponRecord[];
}

export interface CreateCouponInput {
  tenantId: string;
  code: string;
  type: CouponType;
  value: number;
  min_order?: number;
  max_uses?: number | null;
  expires_at?: string | null;
}

export async function createCoupon(input: CreateCouponInput): Promise<CouponRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("coupons")
    .insert({
      tenant_id: input.tenantId,
      code: input.code.trim().toUpperCase(),
      type: input.type,
      value: input.value,
      min_order: input.min_order ?? 0,
      max_uses: input.max_uses ?? null,
      expires_at: input.expires_at ?? null,
      is_active: true,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data as CouponRecord;
}

export async function updateCoupon(
  id: string,
  patch: Partial<
    Pick<CouponRecord, "code" | "type" | "value" | "min_order" | "max_uses" | "expires_at" | "is_active">
  >
): Promise<CouponRecord> {
  const supabase = getSupabaseClient();
  const payload: Record<string, unknown> = { ...patch, updated_at: new Date().toISOString() };
  if (patch.code !== undefined) payload.code = patch.code.trim().toUpperCase();

  const { data, error } = await supabase
    .from("coupons")
    .update(payload)
    .eq("id", id)
    .select("*")
    .single();

  if (error) throw error;
  return data as CouponRecord;
}

export async function deleteCoupon(id: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from("coupons").delete().eq("id", id);
  if (error) throw error;
}

export async function finalizeOrderLoyalty(
  tenantId: string,
  orderId: string
): Promise<{ points_earned?: number; already_credited?: boolean; skipped?: boolean }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("finalize_order_loyalty", {
    p_tenant_id: tenantId,
    p_order_id: orderId,
  });

  if (error) {
    // Migration not applied yet — don't block order flow
    if (error.code === "42883" || error.message?.includes("finalize_order_loyalty")) {
      return { skipped: true };
    }
    throw error;
  }

  return (data as { points_earned?: number; already_credited?: boolean; skipped?: boolean }) ?? {};
}

export async function resolvePublicAffiliate(
  slug: string,
  code: string,
  sessionToken?: string
): Promise<{ valid: boolean; affiliate_id?: string; code?: string; name?: string }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("resolve_public_affiliate", {
    p_slug: slug,
    p_code: code,
    p_session_token: sessionToken ?? null,
  });

  if (error) throw error;
  return (data as { valid: boolean; affiliate_id?: string; code?: string; name?: string }) ?? { valid: false };
}
